import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, CheckCircle2, Paperclip, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { formatApiError } from "../../lib/api";
import { SERVICE_OPTIONS } from "./siteData";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg";
const blank = { name: "", email: "", phone: "", organisation: "", message: "", website: "" };

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function useCaptcha() {
  const [c, setC] = useState(null);
  const refresh = useCallback(() => api.get("/public/captcha").then((r) => setC(r.data)).catch(() => setC(null)), []);
  useEffect(() => { refresh(); }, [refresh]);
  return [c, refresh];
}

function Done({ refId, onReset }) {
  return (
    <div className="py-10 text-center" data-testid="enquiry-success">
      <CheckCircle2 className="h-12 w-12 text-[#C4A66A] mx-auto" />
      <h3 className="font-serif text-3xl text-[#14263D] mt-4">Thank you — we have it.</h3>
      <p className="text-slate-600 mt-2 text-sm">Reference <span className="font-semibold text-[#14263D]" data-testid="enquiry-ref">{refId}</span>. We'll get back to you shortly.</p>
      <button onClick={onReset} className="mt-6 text-xs uppercase tracking-[0.25em] text-[#14263D] site-link" data-testid="enquiry-new-btn">Send another</button>
    </div>
  );
}

export default function EnquiryForm({ service, kind = "enquiry", position = "", compact = false }) {
  const isCareer = kind === "application";
  const [form, setForm] = useState(blank);
  const [svc, setSvc] = useState(service || (isCareer ? "Careers / Articleship" : ""));
  const [file, setFile] = useState(null);
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [refId, setRefId] = useState(null);
  const [captcha, refreshCaptcha] = useCaptcha();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); e.target.value = ""; return; }
    setFile(f || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!svc) { toast.error("Please choose a service"); return; }
    if (isCareer && !file) { toast.error("Please attach your CV"); return; }
    setSending(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("service", svc);
    fd.append("kind", kind);
    fd.append("position", position);
    fd.append("captcha_token", captcha?.token || "");
    fd.append("captcha_answer", answer);
    if (file) fd.append("file", file);
    try {
      const { data } = await api.post("/public/enquiries", fd);
      setRefId(data.id?.replace("enq_", "VCAJ-").toUpperCase());
      toast.success(isCareer ? "Application received" : "Enquiry received");
      setForm(blank); setFile(null); setAnswer("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
      refreshCaptcha();
    }
  };

  if (refId) return <Done refId={refId} onReset={() => setRefId(null)} />;
  const inputCls = "rounded-none h-11 border-slate-300 focus-visible:ring-[#C4A66A]";

  return (
    <form onSubmit={submit} className={compact ? "" : "bg-white p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(12,23,38,0.6)]"} data-testid="enquiry-form">
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" data-testid="enquiry-honeypot" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name *"><Input required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" /></Field>
        <Field label={isCareer ? "Email *" : "Work email *"}><Input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email-input" /></Field>
        <Field label="Phone *"><Input required minLength={6} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="enquiry-phone-input" /></Field>
        <Field label={isCareer ? "Current firm / college" : "Organisation"}><Input value={form.organisation} onChange={set("organisation")} className={inputCls} data-testid="enquiry-org-input" /></Field>
        <Field label="Service *" className="md:col-span-2">
          <Select value={svc} onValueChange={setSvc}>
            <SelectTrigger className="rounded-none h-11 border-slate-300" data-testid="enquiry-service-select"><SelectValue placeholder="Choose the area you need help with" /></SelectTrigger>
            <SelectContent className="z-[200]">
              {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`enquiry-service-option-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={isCareer ? "Tell us about yourself *" : "Your requirement *"} className="md:col-span-2">
          <Textarea required minLength={5} rows={compact ? 4 : 5} value={form.message} onChange={set("message")} className="rounded-none border-slate-300" data-testid="enquiry-message-input" />
        </Field>
        <div className="md:col-span-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{isCareer ? "CV / Resume *" : "Supporting document (optional)"}</span>
          {file ? (
            <div className="mt-2 flex items-center justify-between border border-[#C4A66A]/60 bg-[#F7F4EC] px-4 h-11 text-sm" data-testid="enquiry-file-chip">
              <span className="flex items-center gap-2 truncate"><Paperclip className="h-4 w-4 text-[#C4A66A]" />{file.name}</span>
              <button type="button" onClick={() => setFile(null)} aria-label="Remove file" data-testid="enquiry-file-remove"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <label className="mt-2 flex items-center gap-3 border border-dashed border-slate-300 hover:border-[#C4A66A] transition-colors px-4 h-11 text-sm text-slate-500 cursor-pointer">
              <Paperclip className="h-4 w-4" /> PDF, Word, Excel or image · max 10 MB
              <input type="file" accept={ACCEPT} onChange={pickFile} className="hidden" data-testid="enquiry-file-input" />
            </label>
          )}
        </div>
        <Field label="Quick check *">
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#14263D] font-semibold whitespace-nowrap" data-testid="enquiry-captcha-question">{captcha?.question || "Loading…"}</span>
            <Input required inputMode="numeric" value={answer} onChange={(e) => setAnswer(e.target.value)} className={`${inputCls} w-24`} data-testid="enquiry-captcha-input" />
            <button type="button" onClick={refreshCaptcha} aria-label="New question" className="text-slate-400 hover:text-[#14263D]" data-testid="enquiry-captcha-refresh"><RefreshCw className="h-4 w-4" /></button>
          </div>
        </Field>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-xs text-slate-500 max-w-sm">Your details are used only to respond to this {isCareer ? "application" : "enquiry"}.</p>
        <button type="submit" disabled={sending || !captcha} className="btn-site-navy h-12 px-8 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="enquiry-submit-btn">
          {sending ? "Sending…" : isCareer ? "Submit application" : "Send enquiry"} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
